import { t } from "i18next";
import React, { useEffect, useState } from "react";
import { Button, Modal, Table } from "react-bootstrap";
import { AttributesService } from "../../attributes/service";
import { ErrorToast, SuccessToast } from "../../../shared/toasters/toasters";
import { useSelector } from "react-redux";
import { IAllStates } from "../../../store/rootReducer";
import { IAttributes } from "../../attributes/names/dto/IAttributes";
import Select, { MultiValue } from "react-select";
import { themeSelect } from "../../../shared/components/Select2";
import { IProductValue } from "../dto/IProducts";
import { ProductsService } from "../service";

interface Props {
  currentProduct: any;
  setCurrentProduct: any;
}

export const PreviewProudctModalAttributes = ({
  currentProduct,
  setCurrentProduct,
}: Props) => {
  const { accessRights, roles } = useSelector(
    (state: IAllStates) => state.auth
  );

  const [categoryAttributes, setCategoryAttributes] = useState<IAttributes[]>();
  const [openAttributeModal, setOpenAttributeModal] = useState(false);
  const [selectedAttributeId, setSelectedAttributeId] = useState<number>(0);
  const [currentAttribute, setCurrentAttribute] = useState<any>({});
  const [showForm, setShowForm] = useState(false);
  const [tempValues, setTempValues] = useState<any[]>([]);

  const userCanAddNewAttribute = accessRights?.includes(
    "workflow_can_attribute_can_create_new_value"
  );
  const userClientDashboard = accessRights?.includes(
    "client_dashboard_can_view"
  );
  const isManager = roles?.includes("ROLE_MANAGER");
  const isAdmin = roles?.includes("ROLE_ADMIN");
  const isCompanyAdmin = roles?.includes("ROLE_COMPANY_ADMIN");

  const getAllAttributes = ({ currentProduct, setCategoryAttributes }: any) => {
    AttributesService.getAllAttributes({
      page: 1,
      perPage: 0,
      category: currentProduct?.category?.id ?? currentProduct?.category,
    })
      .then((response) => {
        response.data.forEach((item) => {
          item.attributeValues
            ?.sort((a, b) => a.id - b.id)
            .push({
              title: `+ Add`,
              isAdd: true,
              id: (item?.attributeValues?.at(-1)?.id ?? 0) + 1,
            });
        });
        setCategoryAttributes(response.data);
      })
      .catch((error) => ErrorToast(error));
  };

  useEffect(() => {
    if (currentProduct?.category) {
      getAllAttributes({ currentProduct, setCategoryAttributes });
    } else if (currentProduct?.category === null) {
      setCategoryAttributes([]);
    } else {
      setCategoryAttributes(undefined);
    }
  }, [currentProduct?.category]);

  const handleSubmit = (event: React.FormEvent, checkNext = false) => {
    event.preventDefault();

    ProductsService.updateProductAttributesById(
      {
        values: currentProduct?.values,
      } as any,
      Number(currentProduct?.id),
      false
    )
      .then((response) => {
        SuccessToast(
          `${t("toasts.successfully_updated")} ${currentProduct.title} `
        );
      })
      .catch((error) => ErrorToast(error));
  };
  const handleSelectAttribute = (
    selectedValue: MultiValue<{
      label: string;
      value: number;
      isAdd: boolean;
    }>
  ) => {
    setTempValues(selectedValue as any);
    const itemToAdd = selectedValue?.findIndex((value) => value.isAdd);
    if (itemToAdd !== -1) {
      setOpenAttributeModal(true);
      setSelectedAttributeId(currentAttribute?.id);
    }
  };

  const handleSaveAttributeValues = (e: any) => {
    let tempArr: IProductValue[] = currentProduct?.values ?? [];
    const attributeId = currentAttribute?.id;
    const addId = tempValues?.find((item) => item.isAdd)?.value;

    const indexToReplace = tempArr?.findIndex(
      (attribute) => attribute.attribute === attributeId
    );

    const filteredValues = tempValues
      ?.map((val) => val.value)
      .filter((val) => val !== addId);

    if (indexToReplace === -1) {
      tempArr?.push({
        attribute: attributeId,
        value: filteredValues,
      });
    } else {
      tempArr[indexToReplace] = {
        ...tempArr[indexToReplace],
        value: filteredValues,
      };
    }

    setCurrentProduct((prev: any) => ({ ...prev, values: tempArr }));
    setShowForm(false);
    setTempValues([]);
    handleSubmit(e as any);
  };

  const accessUserClientDashboard = () => {
    return !(userClientDashboard && !isAdmin && !isCompanyAdmin && !isManager);
  };

  return (
    <div className="table-responsive">
      <Table className="align-middle table-nowrap mb-0">
        <thead className="table-light">
          <tr>
            <th colSpan={2}> {t("side_bar.attributes")}</th>
          </tr>
          <tr>
            <th>{t("global.name")}</th>
            <th>{t("mapping.VALUE")}</th>
          </tr>
        </thead>
        <tbody>
          {categoryAttributes?.map((attribute: any) => {
            const selectedAttributeValue = currentProduct?.values?.find(
              (value: any) => value.attribute === attribute?.id
            );

            const defaultValues = selectedAttributeValue?.value
              ?.map((id: number) =>
                attribute?.attributeValues?.find(
                  (value: any) => value?.id === id
                )
              )
              .filter((item: any) => item);

            const ids = defaultValues?.map((o: { id: any }) => o?.id);
            const filtered = defaultValues?.filter(
              (item: { id: any }, index: number) =>
                !ids?.includes(item?.id, index + 1)
            );

            return (
              <tr key={attribute?.id}>
                <td
                  className="cursor-pointer"
                  onClick={() => {
                    setShowForm(true);
                    setCurrentAttribute(attribute);

                    // Setuje trenutno selektovane vrednosti u Select
                    const selected =
                      currentProduct?.values
                        ?.find((val: any) => val.attribute === attribute?.id)
                        ?.value?.map((id: number) =>
                          attribute?.attributeValues?.find(
                            (value: any) => value?.id === id
                          )
                        )
                        ?.filter((item: any) => item)
                        ?.map((value: any) => ({
                          label: value?.title ?? "-",
                          value: value?.id,
                          isAdd: !!value?.isAdd,
                        })) ?? [];

                    setTempValues(selected);
                  }}
                >
                  {attribute?.title ?? "-"}
                </td>
                <td>
                  {filtered && filtered.length > 0
                    ? filtered.map((val: any) => val?.title).join(", ")
                    : "-"}
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>

      {/* Modal za selekciju atributa */}
      <Modal show={showForm} onHide={() => setShowForm(false)} centered>
        <Modal.Body>
          <label htmlFor="title">{currentAttribute?.title ?? "-"}</label>

          <Select
            className="react-select-field"
            theme={themeSelect}
            value={tempValues}
            options={
              currentAttribute?.attributeValues?.map(
                (value: { title: any; id: any; isAdd: any }) => ({
                  label: value?.title ?? "-",
                  value: value?.id,
                  isAdd: !!value?.isAdd,
                })
              ) ?? []
            }
            isMulti
            isDisabled={!accessUserClientDashboard()}
            onChange={(e) =>
              handleSelectAttribute(
                e as MultiValue<{
                  label: string;
                  value: number;
                  isAdd: boolean;
                }>
              )
            }
            key={`${currentProduct?.id}_${currentAttribute?.id}`}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="info" onClick={() => setShowForm(false)}>
            {t("modals.close")}
          </Button>
          <Button variant="primary" onClick={handleSaveAttributeValues}>
            {t("global.save")}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};
